import { Injectable } from '@angular/core';

import {
  catchError,
  concat,
  interval,
  map,
  Observable,
  of,
  startWith,
  Subject,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { Angulartics2GoogleTagManager } from 'angulartics2';
import { datadogRum } from '@datadog/browser-rum';

import {
  AuthRestaurant,
  AuthToken,
  AuthUser,
  LoginResponse,
  SignupByInviteRequest,
  SignupRequest,
  SignupResponse,
} from '@shared/signup/models';
import { AccountStatus } from '@shared/account/enum';
import { AlertsService } from '@tablesready/frontend-alerts-bar';
import { UsernameService } from './username.service';
import { TokenModel, TokenService } from './token.service';
import { SKIP_AUTH_HTTP_HEADER_NAME } from './auth-http-interceptor.service';
import { LastLocationIdService } from './last-location-id.service';
import {
  numberHearFromToString,
  PaymentPlanCodes,
  SubscriptionTypes,
} from '@shared/restaurant/enums';
import { ApiClientService } from '@tablesready/api-client';
import { StatusService } from '@tablesready/frontend-status-bar';
import { LoggerService } from '@tablesready/frontend-logger';
import { GetTokenApiResponse } from './get-token-api-response.model';

// TODO: rework it, should only get token and store it?
@Injectable({providedIn: 'root'})
export class AuthService {
  private msPerSec = 1000;
  private onLogoutSubject = new Subject<null>();
  private readonly tokenRefreshCheckIntervalInMilliseconds = 10000;
  public readonly onLogOut = this.onLogoutSubject.asObservable();
  private onLoginSubject = new Subject<AuthRestaurant>();
  public readonly tokenRefreshCheckInterval = interval(this.tokenRefreshCheckIntervalInMilliseconds)
    .pipe(
      startWith(-1)
    );
  private readonly refreshGap = 3600000;

  constructor(
    private readonly http: ApiClientService,
    private readonly logger: LoggerService,
    private readonly angulartics2GoogleTagManager: Angulartics2GoogleTagManager,
    private readonly statusService: StatusService,
    private readonly alertsService: AlertsService,
    private readonly tokenService: TokenService,
    private readonly usernameService: UsernameService,
    private readonly lastLocationIdService: LastLocationIdService,
  ) { }

  isAuthenticated(): boolean {
    const token = this.getToken();
    return !!token;
  }

  login(credentials: any): Observable<void> {
    return this.http.post<LoginResponse>('/login', credentials).pipe(
      map(({success, tokens, error}) => {
        if (success) {
          this.setTokensAndApplyLastLocationId(tokens);

          return;
        }
        throw {error: `${error}`};
      }),
      catchError(err => throwError(err.error.error || err.error)),
    );
  }

  logout(): void {
    this.onLogoutSubject.next(null);
    this.statusService.clearAllStatuses();
    this.tokenService.clear();
    this.updateDataLayer();
  }

  logoutWithExpiredSession(): void {
    this.alertsService.addSessionExpiredError('Your session has expired. Please log in again.');
    this.logout();
  }

  signup(user: SignupRequest): Observable<SignupResponse> {
    return this.http
      .post<SignupResponse>('/signup', user)
      .pipe(catchError((err) => throwError(err.error.error)));
  }

  signupByInvite(inviteId: string, body: SignupByInviteRequest): Observable<string> {
    return this.http.post<string>(`/signup/${inviteId}`, body);
  }

  getToken(): string {
    return this.tokenService.getActive()?.token;
  }

  refreshTokens() {
    return this.tokenRefreshCheckInterval.pipe(
      map(() => this.tokenService.getAll().filter(token => Date.now() - token.lastUpdatedAt >= this.refreshGap)),
      switchMap(tokens => concat.apply(null, tokens.map(token => this.refreshToken(token)))),
      catchError(() => {
        this.logoutWithExpiredSession();

        return of(null);
      }),
    );
  }

  setTokensAndApplyLastLocationId(tokens: (AuthToken & {lastUpdatedAt?: number})[]): void {
    this.tokenService.setTokens(tokens);
    const lastLocationId = this.lastLocationIdService.get();
    const tokensWithLocation = tokens.filter(token => !!token.locationId);
    if (tokensWithLocation.length) {
      const tokenByLastLocationId = tokensWithLocation.find(token => token.locationId === lastLocationId);
      const locationId = tokenByLastLocationId?.locationId || tokensWithLocation[0].locationId;
      this.tokenService.setActiveByLocationId(locationId);
      this.lastLocationIdService.set(locationId);
    } else {
      this.lastLocationIdService.remove();
      this.tokenService.setActive(tokens[0]);
    }
  }

  private refreshToken(token: TokenModel) {
    let active = this.tokenService.getActive();

    return this.http.put<AuthToken>(
      '/v.1/token/refresh',
      {tokenLastUpdatedAt: token.lastUpdatedAt},
      {
        headers: {
          Authorization: `Bearer ${token.token}`,
          [SKIP_AUTH_HTTP_HEADER_NAME]: 'true',
        },
      })
      .pipe(
        tap(token => {
          this.tokenService.update(token);
          if (token.locationId === active.locationId) {
            this.tokenService.setActive(token);
          }
        }),
        catchError(() => {
          // TODO: remove single token instead of logout?
          this.logoutWithExpiredSession();

          return of(null);
        }),
      );
  }

  updateDataLayer(user?: AuthUser): void {
    if (this.isAuthenticated() && user && user.location) {
      const { account, location } = user;
      const userId = location.mongoId || location._id;

      this.angulartics2GoogleTagManager.setUsername(userId);
      const created_at = user.created_date
        ? Math.round(user.created_date / this.msPerSec)
        : null;

      let subscriptionType = account.subscriptionType as string;
      if (subscriptionType !== SubscriptionTypes.offline) {
        if(account.recurly?.trial) {
          subscriptionType = 'Trial';
        } else if (location.recurly?.planCode === PaymentPlanCodes.Free) {
          subscriptionType = 'Free';
        }
      }

      const newData: any = {
        event: 'user',
        userId,
        logged_in: true,
        created_at,
        name: location.name,
        email: user.email,
        fullName: user.name,
        subscription_type: subscriptionType,
        expires_at: Math.round(location.expiration_date / this.msPerSec),
        country: location.country || '',
        recurly_state: location.recurly
          ? location.recurly.subscription_state
          : 'not_applicable',
        recurly_email: account.recurly ? account.recurly.email : '',
        recurly_account: account.recurly ? account.recurly.account_code : '',
        enabled: location.enabled,
        domain: window.location.hostname,
        industry: account.industry ? account.industry : '',
        messages_month: location.usage?.total || 0,
        verified: account.status === AccountStatus.verified,
        smsConfirmation: location.messages.smsConfirmation?.enabled || false,
        smsResConfirmation: location.messages.smsResConfirmation?.enabled || false,
        smsCompleted: location.messages.smsCompleted?.enabled || false,
        smsReply: location.messages.smsReply?.enabled || false,
        smsReservationCancellation: location.messages.smsReservationCancellation.enabled || false,
        smsNext: location.messages.smsNext?.enabled || false,
        smsReminder: location.messages.smsReminder?.enabled || false,
        squareEnabled: location.integrations?.square || false,
        hearFrom: account.howDidYouHearAboutUs
          ? {
            hearFrom: numberHearFromToString[account.howDidYouHearAboutUs.hearFrom],
            otherSource: account.howDidYouHearAboutUs.otherSource,
          }
          : null,
        account_id: account.id,
        account_name: account.name,
      };

      const datadogRumData: any = {
        id: userId,
        name: newData.name,
        email: newData.email,
      };

      this.angulartics2GoogleTagManager.pushLayer(newData);
      datadogRum.setUser(datadogRumData);
    } else {
      this.angulartics2GoogleTagManager.setUsername(null);

      this.angulartics2GoogleTagManager.pushLayer({
        event: 'user',
        userId: null,
        logged_in: false,
        email: null,
        created_at: null,
        name: null,
        subscription_type: null,
        expires_at: null,
        country: null,
        recurly_state: null,
        recurly_email: null,
        recurly_account: null,
        enabled: null,
        domain: null,
        industry: null,
        messages_month: null,
        verified: null,
        smsConfirmation: null,
        smsResConfirmation: null,
        smsCompleted: null,
        smsReply: null,
        smsReservationCancellation: null,
        smsNext: null,
        smsReminder: null,
        squareEnabled: null,
      });
    }
  }

  passwordReset(email: string, domain: string) {
    return this.http.post('/passwordReset', { email, domain })
      .pipe(
        catchError(() => throwError('Error resetting password.')),
        map((result: any) => {
          if (!result.success) {
            throw result.error;
          }
        }),
      );
  }

  getAnonToken(username: string = this.usernameService.getUsername()) {
    const token = this.tokenService.getAnonToken();
    if (token) {
      return of(token);
    }

    return this.getApiAnonToken(username).pipe(
      tap(result => this.tokenService.setAnonToken(result.token, result.expiresIn)),
      map(result => result.token),
    );
  }

  private getApiAnonToken(username: string): Observable<GetTokenApiResponse> {
    return this.http.get<GetTokenApiResponse>(`/public/token/${username}`).pipe(
      map(result => {
        if (!result.success) {
          throw result.error;
        }
        return result;
      }),
      map((result: GetTokenApiResponse) => {
        // Translate it in ms units
        return { ...result, expiresIn: result.expiresIn * 1000 };
      }),
      catchError(error => {
        this.logger.warn('error getting public token:', error);
        return throwError(error);
      })
    );
  }
}
